import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { useRemoveTile } from "./useRemoveTile";

export interface UseTileDismissOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  documentId?: string;
}

function logDismissDebug(tile: ReviewTileItem, options?: UseTileDismissOptions): void {
  console.log("Debug dismiss:", {
    hasDocumentId: !(options?.documentId == null),
    documentId: options?.documentId,
    hasTileId: tile.originalData?.tile_id !== undefined,
    tileId: tile.originalData?.tile_id,
    hasQuery: !(tile.query == null),
    query: tile.query,
    originalDataKeys: tile.originalData ? Object.keys(tile.originalData) : "no originalData",
  });
}

async function executeDismissSingle(
  tile: ReviewTileItem,
  options: UseTileDismissOptions | undefined,
  removeTileMutation: ReturnType<typeof useRemoveTile>
): Promise<boolean> {
  try {
    const tileId = tile.originalData?.tile_id;
    logDismissDebug(tile, options);

    if (options?.documentId != null && tileId !== undefined && tile.query != null) {
      await removeTileMutation.mutateAsync({
        document_id: [options.documentId],
        query_term: tile.query,
        tile_id: tileId,
      });
    } else {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    return true;
  } catch (error: unknown) {
    console.error("Error dismissing tile:", error);
    return false;
  }
}

export const useTileDismiss = (options?: UseTileDismissOptions) => {
  const [dismissingTileId, setDismissingTileId] = useState<string | null>(null);

  const removeTileMutation = useRemoveTile({
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

  const dismissSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setDismissingTileId(tile.id);
    const result = await executeDismissSingle(tile, options, removeTileMutation);
    setDismissingTileId(null);
    return result;
  };

  return {
    dismissingTileId,
    dismissSingleTile,
  };
};
